import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    productId: Number,
    metalType: { type: String, default: 'wg' },
    viewType: { type: String, default: 'main' },
    frameCount: { type: Number, default: 120 },
    basePath: String,
    autoplay: { type: Boolean, default: true },
    autoplaySpeed: { type: Number, default: 60 } // milliseconds per frame
  }

  static targets = ["image", "playButton"]

  connect() {
    this.currentFrame = 0
    this.isDragging = false
    this.lastX = 0
    this.isLoading = false
    this.isPlaying = false
    this.autoplayInterval = null
    
    this.preloadedImages = []
    this.setupEventListeners()
    this.preloadImages()
    
    // Start autoplay after initial images are loaded
    if (this.autoplayValue) {
      setTimeout(() => this.startAutoplay(), 500)
    }
  }

  disconnect() {
    this.removeEventListeners()
    this.stopAutoplay()
  }

  setupEventListeners() {
    // Mouse events
    this.imageTarget.addEventListener('mousedown', this.startDrag.bind(this))
    this.imageTarget.addEventListener('mousemove', this.drag.bind(this))
    this.imageTarget.addEventListener('mouseup', this.stopDrag.bind(this))
    this.imageTarget.addEventListener('mouseleave', this.stopDrag.bind(this))
    
    // Touch events for mobile
    this.imageTarget.addEventListener('touchstart', this.handleTouchStart.bind(this))
    this.imageTarget.addEventListener('touchmove', this.handleTouchMove.bind(this))
    this.imageTarget.addEventListener('touchend', this.stopDrag.bind(this))
    
    // Prevent context menu (right-click)
    this.imageTarget.addEventListener('contextmenu', (e) => e.preventDefault())
    
    // Prevent image dragging
    this.imageTarget.style.userSelect = 'none'
    this.imageTarget.style.webkitUserDrag = 'none'
    this.imageTarget.draggable = false
  }

  removeEventListeners() {
    this.imageTarget.removeEventListener('mousedown', this.startDrag)
    this.imageTarget.removeEventListener('mousemove', this.drag)
    this.imageTarget.removeEventListener('mouseup', this.stopDrag)
    this.imageTarget.removeEventListener('mouseleave', this.stopDrag)
    this.imageTarget.removeEventListener('touchstart', this.handleTouchStart)
    this.imageTarget.removeEventListener('touchmove', this.handleTouchMove)
    this.imageTarget.removeEventListener('touchend', this.stopDrag)
  }

  startDrag(e) {
    this.isDragging = true
    this.lastX = e.clientX || (e.touches && e.touches[0].clientX)
    this.imageTarget.style.cursor = 'grabbing'
    this.stopAutoplay() // Stop autoplay when user starts dragging
  }

  drag(e) {
    if (!this.isDragging) return
    
    e.preventDefault()
    const currentX = e.clientX || (e.touches && e.touches[0].clientX)
    const deltaX = currentX - this.lastX
    
    // Sensitivity: how many pixels to drag for one frame change
    const sensitivity = 3
    const frameDelta = Math.floor(deltaX / sensitivity)
    
    if (frameDelta !== 0) {
      this.currentFrame = (this.currentFrame - frameDelta + this.frameCountValue) % this.frameCountValue
      this.updateImage()
      this.lastX = currentX
    }
  }

  stopDrag() {
    this.isDragging = false
    this.imageTarget.style.cursor = 'grab'
  }

  handleTouchStart(e) {
    e.preventDefault()
    this.startDrag(e.touches[0])
  }

  handleTouchMove(e) {
    e.preventDefault()
    this.drag(e.touches[0])
  }

  updateImage() {
    const frameNumber = String(this.currentFrame).padStart(5, '0')
    const imagePath = this.getImagePath(frameNumber)

    // Always switch immediately; browser/cache handles actual image fetch timing.
    this.imageTarget.src = imagePath
  }

  getImagePath(frameNumber) {
    if (this.basePathValue) {
      // For video2 - simpler path structure
      return `${this.basePathValue}/img${frameNumber}.jpg`
    } else {
      // For video - complex path structure
      return `/jewelry-assets/vi/${this.productIdValue}/video/${this.viewTypeValue}/${this.metalTypeValue}/img${frameNumber}.jpg`
    }
  }

  preloadImages() {
    // Preload first few images for smoother initial experience
    const initialLoad = Math.min(30, this.frameCountValue)
    
    for (let i = 0; i < initialLoad; i++) {
      this.preloadImage(i)
    }
    
    // Load remaining images in background
    setTimeout(() => {
      for (let i = initialLoad; i < this.frameCountValue; i++) {
        this.preloadImage(i)
      }
    }, 500)
  }

  preloadImage(frameIndex) {
    const frameNumber = String(frameIndex).padStart(5, '0')
    const img = new Image()
    img.src = this.getImagePath(frameNumber)
    this.preloadedImages[frameIndex] = img
  }

  // Method to switch metal type/view type (called from buttons via Stimulus action)
  switchView(event) {
    const button = event.currentTarget
    const metalType = button.dataset.metal
    const viewType = button.dataset.view
    
    this.changeView(metalType, viewType)
  }

  // Method to switch metal type/view type (can also be called programmatically)
  changeView(metalType, viewType) {
    this.metalTypeValue = metalType
    this.viewTypeValue = viewType
    this.currentFrame = 0
    this.preloadedImages = []
    this.updateImage()
    this.preloadImages()

    // Restart autoplay if it was playing
    if (this.isPlaying) {
      this.stopAutoplay()
      setTimeout(() => this.startAutoplay(), 300)
    }
  }

  // Autoplay methods
  startAutoplay() {
    if (this.isPlaying) return
    
    this.isPlaying = true
    this.autoplayInterval = setInterval(() => {
      this.currentFrame = (this.currentFrame + 1) % this.frameCountValue
      this.updateImage()
    }, this.autoplaySpeedValue)
    
    // Update play button icon if it exists
    this.updatePlayButtonIcon()
  }

  stopAutoplay() {
    if (!this.isPlaying) return
    
    this.isPlaying = false
    if (this.autoplayInterval) {
      clearInterval(this.autoplayInterval)
      this.autoplayInterval = null
    }
    
    // Update play button icon if it exists
    this.updatePlayButtonIcon()
  }

  toggleAutoplay() {
    if (this.isPlaying) {
      this.stopAutoplay()
    } else {
      this.startAutoplay()
    }
  }

  updatePlayButtonIcon() {
    if (!this.hasPlayButtonTarget) return
    
    const icon = this.playButtonTarget.querySelector('i')
    if (icon) {
      if (this.isPlaying) {
        icon.className = 'bi bi-pause-circle'
      } else {
        icon.className = 'bi bi-play-circle'
      }
    }
  }
}
